import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles } from "lucide-react";
import { getWorkflowRecommendations } from "@/lib/workflowRecommendations";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";

interface WorkflowSuggestionsProps {
  userId: string;
  unaId?: string;
  tier: string;
}

export const WorkflowSuggestions = ({ userId, unaId, tier }: WorkflowSuggestionsProps) => {
  const navigate = useNavigate();

  const { data: suggestions, isLoading } = useQuery({
    queryKey: ['workflow-suggestions', userId, unaId],
    queryFn: async () => {
      if (!unaId) return [];

      // Get user context
      const { data: progress } = await supabase
        .from('user_fulfillment_progress')
        .select('onboarding_data')
        .eq('user_id', userId)
        .single();

      const onboardingData = progress?.onboarding_data as any || {};

      const recommendations = await getWorkflowRecommendations({
        userId,
        userTier: tier,
        hasSpouse: onboardingData.marital_status === 'married',
        hasChildren: onboardingData.has_children === true,
        hasLLC: onboardingData.has_llc === true,
      });

      // Fetch workflow details
      const { data: workflows } = await supabase
        .from('workflows')
        .select('*')
        .in('id', recommendations);

      return workflows || [];
    },
    enabled: !!userId && !!unaId,
  });

  const getIconComponent = (iconName: string) => {
    const Icon = Icons[iconName as keyof typeof Icons] as any;
    return Icon ? <Icon className="h-5 w-5" /> : <Icons.FileText className="h-5 w-5" />;
  };

  if (isLoading || !suggestions || suggestions.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <CardTitle>Recommended for You</CardTitle>
        </div>
        <CardDescription>
          Based on your profile and UNA status
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {suggestions.map((workflow: any) => (
          <div
            key={workflow.id}
            className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/50 transition-colors"
          >
            <div className="flex items-center gap-3 flex-1">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                {getIconComponent(workflow.icon_name)}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium">{workflow.title}</h4>
                <p className="text-sm text-muted-foreground truncate">
                  {workflow.estimated_time} • {workflow.difficulty}
                </p>
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => navigate(`/workflows?id=${workflow.id}`)}
            >
              Start
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
